/**
 * Shared bits for embedded YouTube players (Classroom lesson player, Shorts
 * feed).
 *
 * - `loadYouTubeApi()` loads https://www.youtube.com/iframe_api once and
 *   resolves with `window.YT` (safe to call from many components at once).
 * - `disableCaptions(player)` switches the player's caption track off. Our
 *   videos carry burned-in captions, so YouTube's own layer doubled them
 *   (Master, 2026-09-05). There is no documented "force off" player param
 *   (`cc_load_policy=1` only forces ON), so this unloads the caption modules
 *   the way the player itself does when a viewer turns captions off. Pair it
 *   with `cc_load_policy: 3` in playerVars — undocumented, but keeps the
 *   default-on behaviour from kicking in before onReady fires.
 */

declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let ytApiPromise: Promise<any> | null = null;

export function loadYouTubeApi(): Promise<any> {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const s = document.createElement("script");
        s.src = "https://www.youtube.com/iframe_api";
        s.async = true;
        document.head.appendChild(s);
      }
    });
  }
  return ytApiPromise;
}

export function disableCaptions(player: any) {
  try { player?.unloadModule?.("captions"); } catch { /* older player builds */ }
  try { player?.unloadModule?.("cc"); } catch { /* noop */ }
}

/** Query params for a plain `<iframe src=…/embed/…>` where the API isn't used. */
export const CAPTIONS_OFF_PARAM = "cc_load_policy=3";
